import React from 'react';

const PerformanceDetailsModal = ({
    isOpen,
    onClose,
    metricsList,
    navigationTiming,
    formatFieldName,
    formatValue,
    tableHeaderStyle,
    tableCellStyle,
    coreWebVitals,
    coreWebVitalsSummary = [],
    thirdPartySplit,
    blockingScripts = [],
    coverage,
    formatDurationMs
}) => {
    if (!isOpen) {
        return null;
    }

    const formatDuration = typeof formatDurationMs === 'function'
        ? formatDurationMs
        : (value) => {
            const numeric = Number(value);

            if (!Number.isFinite(numeric)) {
                return '—';
            }

            if (numeric >= 1000) {
                const seconds = numeric / 1000;
                return seconds >= 10 ? `${seconds.toFixed(1)} s` : `${seconds.toFixed(2)} s`;
            }

            if (numeric <= 0) {
                return '0 ms';
            }

            if (numeric < 1) {
                return `${numeric.toFixed(2)} ms`;
            }

            return `${Math.round(numeric)} ms`;
        };

    const formatBytes = (bytes) => {
        const numeric = Number(bytes);

        if (!Number.isFinite(numeric) || numeric < 0) {
            return '—';
        }

        if (numeric === 0) {
            return '0 B';
        }

        const units = ['B', 'KB', 'MB', 'GB'];
        const exponent = Math.min(Math.floor(Math.log10(numeric) / 3), units.length - 1);
        const value = numeric / (1000 ** exponent);
        const decimals = value >= 10 ? 1 : 2;

        return `${value.toFixed(decimals)} ${units[exponent]}`;
    };

    const coreVitalsSummaryItems = Array.isArray(coreWebVitalsSummary)
        ? coreWebVitalsSummary.filter(item => item && item.displayValue !== '—')
        : [];

    const hasCoreWebVitalsSummary = coreVitalsSummaryItems.length > 0;

    const topClsEntries = Array.isArray(coreWebVitals?.clsEntries)
        ? [...coreWebVitals.clsEntries]
            .filter(entry => entry && typeof entry.value === 'number')
            .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
            .slice(0, 5)
        : [];

    const navigationDetails = coreWebVitals && coreWebVitals.navigation
        ? Object.entries(coreWebVitals.navigation)
        : [];

    const hasThirdPartySplit = Boolean(thirdPartySplit && (thirdPartySplit.firstParty || thirdPartySplit.thirdParty));

    const coverageJavascript = coverage?.javascript;
    const coverageFiles = Array.isArray(coverageJavascript?.files)
        ? [...coverageJavascript.files].sort((a, b) => (b.usedBytes ?? 0) - (a.usedBytes ?? 0)).slice(0, 10)
        : [];

    const formatNavigationValue = (key, value) => {
        if (value === null || value === undefined) {
            return '—';
        }

        if (typeof value === 'number') {
            const lowerKey = key.toLowerCase();

            if (
                lowerKey.includes('time') ||
                lowerKey.includes('duration') ||
                lowerKey.endsWith('start') ||
                lowerKey.endsWith('end')
            ) {
                return formatDuration(value);
            }

            return formatValue(value);
        }

        return value;
    };

    const formatClsSources = (sources = []) => {
        if (!Array.isArray(sources) || sources.length === 0) {
            return '—';
        }

        const nodes = sources
            .map(source => source?.node)
            .filter(Boolean)
            .slice(0, 3);

        if (nodes.length === 0) {
            return '—';
        }

        return nodes.join(', ');
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(7, 7, 10, 0.92)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                padding: '24px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1200px',
                    maxHeight: '860px',
                    backgroundColor: '#121212',
                    borderRadius: '10px',
                    boxShadow: '0 18px 60px rgba(0, 0, 0, 0.55)',
                    border: '1px solid rgba(100, 181, 246, 0.35)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '14px',
                        right: '14px',
                        backgroundColor: 'rgba(220, 53, 69, 0.92)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '42px',
                        height: '42px',
                        fontSize: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 20px rgba(220, 53, 69, 0.45)',
                        transition: 'transform 0.2s ease, background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'rgba(207, 46, 60, 0.95)';
                        e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.92)';
                        e.target.style.transform = 'scale(1)';
                    }}
                >
                    ×
                </button>
                <div
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        backgroundColor: '#0c0c0c',
                        padding: '24px'
                    }}
                >
                    <h3
                        style={{
                            margin: '0 0 24px 0',
                            color: '#e0e0e0',
                            fontWeight: '600'
                        }}
                    >
                        Performance Details
                    </h3>

                    {hasCoreWebVitalsSummary && (
                        <div
                            style={{
                                marginBottom: '30px',
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                Core Web Vitals
                            </h4>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '14px'
                                }}
                            >
                                {coreVitalsSummaryItems.map((item) => (
                                    <div
                                        key={item.key}
                                        style={{
                                            backgroundColor: '#202020',
                                            border: '1px solid rgba(100, 181, 246, 0.18)',
                                            borderRadius: '8px',
                                            padding: '14px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '10px'
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                color: '#9a9a9a',
                                                textTransform: 'uppercase',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            {item.shortLabel}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '16px',
                                                color: '#e0e0e0',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {item.displayValue}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '12px',
                                                color: '#b8b8b8',
                                                lineHeight: '1.4'
                                            }}
                                        >
                                            {item.label}
                                        </div>
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px',
                                                backgroundColor: item.statusColor,
                                                color: item.statusTextColor,
                                                alignSelf: 'flex-start'
                                            }}
                                        >
                                            {item.statusLabel}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {metricsList.length > 0 && (
                        <div
                            style={{
                                marginBottom: '30px',
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                Metric Values
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        minWidth: '480px'
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>Metric</th>
                                            <th style={tableHeaderStyle}>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {metricsList.map(({ name, value }) => (
                                            <tr key={name}>
                                                <td style={tableCellStyle}>{name}</td>
                                                <td style={tableCellStyle}>{formatValue(value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {navigationTiming && (
                        <div
                            style={{
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                Navigation Timing
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        minWidth: '480px'
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>Event</th>
                                            <th style={tableHeaderStyle}>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(navigationTiming).map(([key, value]) => (
                                            <tr key={key}>
                                                <td style={tableCellStyle}>{formatFieldName(key)}</td>
                                                <td style={tableCellStyle}>{formatValue(value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {navigationDetails.length > 0 && (
                        <div
                            style={{
                                marginBottom: '30px',
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                Core Web Vitals Navigation
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        minWidth: '480px'
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>Metric</th>
                                            <th style={tableHeaderStyle}>Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {navigationDetails.map(([key, value]) => (
                                            <tr key={key}>
                                                <td style={tableCellStyle}>{formatFieldName(key)}</td>
                                                <td style={tableCellStyle}>{formatNavigationValue(key, value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {topClsEntries.length > 0 && (
                        <div
                            style={{
                                marginBottom: '30px',
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                Largest Layout Shifts
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        minWidth: '520px'
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>Shift Value</th>
                                            <th style={tableHeaderStyle}>Start Time</th>
                                            <th style={tableHeaderStyle}>Sources</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topClsEntries.map((entry, index) => (
                                            <tr key={`${entry.startTime}-${index}`}>
                                                <td style={tableCellStyle}>{entry.value.toFixed(4)}</td>
                                                <td style={tableCellStyle}>{formatDuration(entry.startTime)}</td>
                                                <td style={{
                                                    ...tableCellStyle,
                                                    maxWidth: '420px',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {formatClsSources(entry.sources)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {hasThirdPartySplit && (
                        <div
                            style={{
                                marginBottom: '30px',
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                Request Distribution
                            </h4>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                    gap: '14px'
                                }}
                            >
                                {[{
                                    key: 'firstPartyRequests',
                                    label: 'First-party Requests',
                                    value: thirdPartySplit.firstParty?.requests,
                                    formatter: formatValue
                                }, {
                                    key: 'firstPartyBytes',
                                    label: 'First-party Bytes',
                                    value: thirdPartySplit.firstParty?.bytes,
                                    formatter: formatBytes
                                }, {
                                    key: 'thirdPartyRequests',
                                    label: 'Third-party Requests',
                                    value: thirdPartySplit.thirdParty?.requests,
                                    formatter: formatValue
                                }, {
                                    key: 'thirdPartyBytes',
                                    label: 'Third-party Bytes',
                                    value: thirdPartySplit.thirdParty?.bytes,
                                    formatter: formatBytes
                                }, {
                                    key: 'totalBytes',
                                    label: 'Total Bytes',
                                    value: thirdPartySplit.totalBytes,
                                    formatter: formatBytes
                                }].map(item => (
                                    <div
                                        key={item.key}
                                        style={{
                                            backgroundColor: '#202020',
                                            border: '1px solid rgba(100, 181, 246, 0.18)',
                                            borderRadius: '8px',
                                            padding: '14px'
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '11px',
                                                color: '#909090',
                                                textTransform: 'uppercase',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px',
                                                marginBottom: '6px'
                                            }}
                                        >
                                            {item.label}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '18px',
                                                color: '#e0e0e0',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {item.value === null || item.value === undefined ? '—' : item.formatter(item.value)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {blockingScripts.length > 0 && (
                        <div
                            style={{
                                marginBottom: '30px',
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                Blocking Scripts
                            </h4>
                            <div style={{ overflowX: 'auto' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        minWidth: '520px'
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={tableHeaderStyle}>Script</th>
                                            <th style={tableHeaderStyle}>Status</th>
                                            <th style={tableHeaderStyle}>Start</th>
                                            <th style={tableHeaderStyle}>Duration</th>
                                            <th style={tableHeaderStyle}>Response End</th>
                                            <th style={tableHeaderStyle}>Transfer Size</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blockingScripts.map((script, index) => (
                                            <tr key={`${script.name}-${index}`}>
                                                <td style={{
                                                    ...tableCellStyle,
                                                    maxWidth: '360px',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {script.name || '—'}
                                                </td>
                                                <td style={tableCellStyle}>{script.renderBlockingStatus || '—'}</td>
                                                <td style={tableCellStyle}>{formatDuration(script.startTime)}</td>
                                                <td style={tableCellStyle}>{formatDuration(script.duration)}</td>
                                                <td style={tableCellStyle}>{formatDuration(script.responseEnd)}</td>
                                                <td style={tableCellStyle}>{formatBytes(script.transferSize)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {coverageJavascript && (
                        <div
                            style={{
                                marginBottom: '30px',
                                backgroundColor: '#181818',
                                borderRadius: '8px',
                                border: '1px solid rgba(100, 181, 246, 0.12)',
                                padding: '20px'
                            }}
                        >
                            <h4
                                style={{
                                    margin: '0 0 16px 0',
                                    color: '#d8d8d8',
                                    fontWeight: '600'
                                }}
                            >
                                JavaScript Coverage
                            </h4>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                    gap: '14px',
                                    marginBottom: coverageFiles.length > 0 ? '16px' : 0
                                }}
                            >
                                {[{
                                    key: 'usedBytes',
                                    label: 'Used Bytes',
                                    value: coverageJavascript.usedBytes
                                }, {
                                    key: 'totalBytes',
                                    label: 'Total Bytes',
                                    value: coverageJavascript.totalBytes
                                }, {
                                    key: 'usagePercent',
                                    label: 'Usage %',
                                    value: coverageJavascript.totalBytes
                                        ? ((coverageJavascript.usedBytes / coverageJavascript.totalBytes) * 100)
                                        : null,
                                    formatter: (val) => `${val.toFixed(1)}%`
                                }].map(item => (
                                    <div
                                        key={item.key}
                                        style={{
                                            backgroundColor: '#202020',
                                            border: '1px solid rgba(100, 181, 246, 0.18)',
                                            borderRadius: '8px',
                                            padding: '14px'
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: '11px',
                                                color: '#909090',
                                                textTransform: 'uppercase',
                                                fontWeight: '600',
                                                letterSpacing: '0.5px',
                                                marginBottom: '6px'
                                            }}
                                        >
                                            {item.label}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '18px',
                                                color: '#e0e0e0',
                                                fontWeight: '600'
                                            }}
                                        >
                                            {item.value === null || item.value === undefined
                                                ? '—'
                                                : item.formatter
                                                    ? item.formatter(item.value)
                                                    : formatBytes(item.value)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {coverageFiles.length > 0 && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table
                                        style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            minWidth: '600px'
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th style={tableHeaderStyle}>File</th>
                                                <th style={tableHeaderStyle}>Used</th>
                                                <th style={tableHeaderStyle}>Total</th>
                                                <th style={tableHeaderStyle}>Usage</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {coverageFiles.map((file, index) => {
                                                const usagePercent = file.totalBytes
                                                    ? ((file.usedBytes / file.totalBytes) * 100)
                                                    : null;

                                                return (
                                                    <tr key={`${file.url}-${index}`}>
                                                        <td style={{
                                                            ...tableCellStyle,
                                                            maxWidth: '420px',
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {file.url || '—'}
                                                        </td>
                                                        <td style={tableCellStyle}>{formatBytes(file.usedBytes)}</td>
                                                        <td style={tableCellStyle}>{formatBytes(file.totalBytes)}</td>
                                                        <td style={tableCellStyle}>
                                                            {usagePercent === null ? '—' : `${usagePercent.toFixed(1)}%`}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PerformanceDetailsModal;

